import React from "react";
import { useTranslation } from "react-i18next";
import { ExpenditureTypeSelector } from "./expenditureTypeSelector";
import classNames from "classnames";
import { TaskStatus } from "@scenes/taskStatus";
import { Assignable } from "@planzone/dto";
import { useEditTask } from "@planzone/view-models";
import { TagList } from "@common/tagList";
import { Props } from "./types";
import { NameForm } from "./nameForm";
import { RichTextInput } from "@common/richTextInput";
import styles from "./styles.scss";
import { useModal } from "@root/client/modal";
import { UseCases } from "@planzone/core";
import { ExpenditureType } from "./expenditureTypeSelector/types";

function Details({ task, error, readOnly }: Props) {
  const { t } = useTranslation();
  const [description, setDescription] = React.useState(task.description);
  const { isRequestPending, editTask } = useEditTask(task);
  const [isEditable, setIsEditable] = React.useState(false);
  const { pushView } = useModal();
  const toggleTitleModification = () => setIsEditable(!isEditable);
  const isCompleted = task.percentComplete === 100;

  return (
    <div className={classNames(styles.details)}>
      {!readOnly && isEditable ? (
        <NameForm
          task={task}
          isInvalid={error.emptyName || error.duplicateName}
          hide={toggleTitleModification}
        />
      ) : (
        <h1 className={styles.name} onClick={toggleTitleModification}>
          {task.name}
        </h1>
      )}
      <div className={styles.container}>
        <div className={styles.item}>
          <span className={styles.label}>Type</span>
          <span className={styles.value}>{t("taskRetrieval.type")}</span>
        </div>

        <div className={styles.item}>
          <span className={styles.label}>{t("taskRetrieval.status")}</span>
          <span className={styles.value}>
            {task.status && (
              <TaskStatus
                status={task.status}
                project={task.project}
                canAddStatus
                selectStatus={(status) =>
                  status.percentComplete === 100
                    ? pushView({
                        key: UseCases.editTaskStatus,
                        data: {
                          task,
                          status,
                          project: task.project,
                        },
                      })
                    : editTask({
                        task,
                        nextTask: {
                          ...task,
                          status,
                        },
                      })
                }
                isCompleted={isCompleted}
              />
            )}
          </span>
        </div>

        <div className={styles.item}>
          <span className={styles.label}>{t("taskRetrieval.advancement")}</span>
          <span
            className={styles.percentageText}
            style={{ color: task.status.color }}
          >
            {task.percentComplete + "%"}
          </span>
        </div>

        <div className={styles.item}>
          <ExpenditureTypeSelector
            value={
              task.attributes?.["expenditureType"] as
                | ExpenditureType
                | undefined
            }
            disabled={!!readOnly}
            submit={(value) =>
              editTask({
                task,
                nextTask: {
                  ...task,
                  attributes: {
                    ...task.attributes,
                    expenditureType: value ?? "",
                  },
                },
              })
            }
          />
        </div>

        <div className={styles.item}>
          <span className={styles.label}>{t("taskRetrieval.tags")}</span>
          <span className={styles.tagsValue}>
            <TagList
              id={task.uuid}
              tags={task.tags}
              type={Assignable.TASK}
              project={task.project}
              readOnly={readOnly}
            />
          </span>
        </div>
      </div>

      <div className={styles.description}>
        <h3 className={styles.label}>{t("taskRetrieval.description")}</h3>
        <RichTextInput
          value={description || ""}
          maxLength={10000}
          placeholder={t("taskRetrieval.description")}
          input={setDescription}
          onChange={() =>
            !isRequestPending &&
            editTask({
              task,
              nextTask: {
                ...task,
                description,
              },
            })
          }
          isDisabled={readOnly}
        />
      </div>
    </div>
  );
}

export { Details };
